import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { cn } from "@/modules/lib/utils"
import SearchEmpty from "@images/empty/search_empty.svg"
import { word } from "@modules/core/utils/i18n"
import useDebounce from "@modules/hooks/useDebounce"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { ProgramSasPendingErrorView } from "@modules/programs/programSas/components/programSasPendingErrorView"
import { useProgramSasRequests } from "@modules/programs/programSas/hooks/useProgramSasRequests"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { EmptyView } from "@modules/ui/components/emptyView"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { Search, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramSasPendingPaymentViewProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSasPendingPaymentView({ programId, className, ...props }: ProgramSasPendingPaymentViewProps) {
  const [searchTerms, setsearchTerms] = useState<string>("")
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const debouncedSearchTerms = useDebounce(searchTerms, 300)

  const {
    data: { content: subscribers },
    isLoading: isLoadingSubscribers,
    isFetching: isFetchingSubscribers,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProgramSasRequests(programId, ProgramSubscriptionStatus.PENDING_PAYMENT, debouncedSearchTerms)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "100px",
      threshold: 0.1,
    })

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)

      // Check if the element is already visible on mount (for large screens)
      const rect = currentRef.getBoundingClientRect()
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0
      if (isVisible && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    return () => observer.disconnect()
  }, [handleIntersection, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className={cn("flex grow flex-col gap-4", className)} {...props}>
      <InputGroup>
        <InputGroupInput
          placeholder={word("program.subscribersList.search.placeholder")}
          onChange={(e) => setsearchTerms(e.currentTarget.value)}
          value={searchTerms}
        />
        <InputGroupAddon align="inline-start">
          <Search />
        </InputGroupAddon>
        {searchTerms && (
          <InputGroupAddon align="inline-end">
            <X className="size-4 cursor-pointer" onClick={() => setsearchTerms("")} />
          </InputGroupAddon>
        )}
      </InputGroup>

      <div className="mb-6 flex flex-col">
        {isLoadingSubscribers ? (
          <LoadingSpinner />
        ) : subscribers.length > 0 ? (
          <div className="flex flex-col gap-3">
            {subscribers.map((subscriber) => (
              <div className="flex" key={subscriber.id}>
                <div className="flex items-center">
                  <ProfileAvatar
                    className="mr-2 size-12"
                    profile={{ avatar: subscriber.avatar ?? null, username: subscriber.firstname ?? "" }}
                  />
                  <div className="flex flex-col">
                    <Text fontStyle="LL" className="mb-1">{`${subscriber.firstname} ${subscriber.lastname}`}</Text>
                    <Text fontStyle="BS" className="text-textDefault-caption">
                      {`@${subscriber.username}`}
                    </Text>
                  </div>
                </div>
              </div>
            ))}
            {(hasNextPage || isFetchingNextPage) && (
              <div ref={loadMoreRef} className="flex h-auto w-full items-center justify-center py-4">
                <Text fontStyle="BM">{word("global.loading")}</Text>
              </div>
            )}
          </div>
        ) : subscribers.length === 0 && !isFetchingSubscribers && searchTerms.length > 0 ? (
          <EmptyView
            image={{ src: SearchEmpty }}
            title={word("explore.search.no_result.title", { searchTerms })}
            description={word("explore.search.no_result.message")}
            flexGrow={false}
            textAlign="left"
            imagePosition="after"
            style={{
              backgroundColor: "var(--surface-background)",
              display: "flex w-full p-0",
            }}
          />
        ) : isError ? (
          <ProgramSasPendingErrorView />
        ) : null}
      </div>
    </div>
  )
}
